import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useStreams, useUnassignedStories, useStreamSuggestions } from '../../hooks/useStreams';
import { apiClient } from '../../api/client';
import { Layers, FileText, Clock, ChevronRight, Plus } from 'lucide-react';

const streamTypeColors = {
  event: 'bg-blue-100 text-blue-800',
  ongoing: 'bg-purple-100 text-purple-800',
  thematic: 'bg-green-100 text-green-800',
};

export function StreamsPage() {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const orgPrefix = orgSlug ? `/${orgSlug}` : '';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: streamsData, isLoading: streamsLoading } = useStreams();
  const { data: unassignedData, isLoading: unassignedLoading } = useUnassignedStories();

  const streams = streamsData?.streams || [];
  const unassignedStories = unassignedData?.stories || [];

  // Stream suggestions
  const { data: suggestionsData } = useStreamSuggestions(50);
  const suggestions = suggestionsData?.suggestions || [];
  const suggestionMap = new Map(suggestions.map(s => [s.storyId, s]));

  // Assigning state
  const [assigningStoryId, setAssigningStoryId] = useState<string | null>(null);

  const handleAssignToStream = async (storyId: string, streamId: string) => {
    setAssigningStoryId(storyId);
    try {
      await apiClient.patch(`/stories/${storyId}/stream`, { streamId });
      queryClient.invalidateQueries({ queryKey: ['stories'] });
      queryClient.invalidateQueries({ queryKey: ['streams'] });
    } catch (err) {
      alert('Failed to assign: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setAssigningStoryId(null);
    }
  };

  // New stream form state
  const [showNewStreamForm, setShowNewStreamForm] = useState(false);
  const [newStreamTitle, setNewStreamTitle] = useState('');
  const [newStreamDescription, setNewStreamDescription] = useState('');
  const [newStreamType, setNewStreamType] = useState<'event' | 'ongoing' | 'thematic'>('event');
  const [isCreating, setIsCreating] = useState(false);

  const handleCreateStream = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newStreamTitle.trim()) return;

    setIsCreating(true);
    try {
      const result = await apiClient.post<{ id: string; slug: string }>('/streams', {
        title: newStreamTitle,
        description: newStreamDescription || undefined,
        streamType: newStreamType,
      });
      queryClient.invalidateQueries({ queryKey: ['streams'] });
      setShowNewStreamForm(false);
      setNewStreamTitle('');
      setNewStreamDescription('');
      setNewStreamType('event');
      navigate(`${orgPrefix}/stream/${result.id}`);
    } catch (err) {
      alert('Failed to create stream: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Layers className="w-6 h-6" />
            Story Streams
          </h1>
          <p className="text-gray-600 mt-1">
            Streams group related stories into ongoing narratives
          </p>
        </div>
        <button
          onClick={() => setShowNewStreamForm(true)}
          className="flex items-center gap-1 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Stream
        </button>
      </div>

      {/* New Stream Form Modal */}
      {showNewStreamForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Create New Stream</h2>
            <form onSubmit={handleCreateStream}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input
                    type="text"
                    value={newStreamTitle}
                    onChange={(e) => setNewStreamTitle(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Stream title..."
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                  <textarea
                    value={newStreamDescription}
                    onChange={(e) => setNewStreamDescription(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    rows={3}
                    placeholder="What is this stream about?"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <div className="flex gap-2">
                    {(['event', 'ongoing', 'thematic'] as const).map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setNewStreamType(type)}
                        className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                          newStreamType === type
                            ? streamTypeColors[type]
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowNewStreamForm(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCreating || !newStreamTitle.trim()}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                >
                  {isCreating ? 'Creating...' : 'Create Stream'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Active Streams */}
      <section className="mb-10">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Active Streams</h2>

        {streamsLoading ? (
          <div className="text-gray-500">Loading streams...</div>
        ) : streams.length === 0 ? (
          <div className="bg-gray-50 rounded-lg p-6 text-center text-gray-500">
            No streams yet. Streams are created when related stories are grouped together.
          </div>
        ) : (
          <div className="space-y-3">
            {streams.map((stream) => (
              <Link
                key={stream.id}
                to={`${orgPrefix}/stream/${stream.id}`}
                className="block bg-white rounded-lg shadow hover:shadow-md transition-shadow p-4"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${streamTypeColors[stream.streamType]}`}>
                        {stream.streamType}
                      </span>
                      {stream.startDate && (
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(stream.startDate).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    <h3 className="font-medium text-gray-900 truncate">{stream.title}</h3>
                    {stream.description && (
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">{stream.description}</p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                      {stream.storyCount !== undefined && (
                        <span className="flex items-center gap-1">
                          <FileText className="w-4 h-4" />
                          {stream.storyCount} {stream.storyCount === 1 ? 'story' : 'stories'}
                        </span>
                      )}
                      {stream.claimCount !== undefined && <span>{stream.claimCount} claims</span>}
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Unassigned Stories */}
      <section>
        <h2 className="text-lg font-semibold text-gray-800 mb-4">
          Unassigned Stories
          {unassignedStories.length > 0 && (
            <span className="ml-2 text-sm font-normal text-gray-500">
              ({unassignedStories.length})
            </span>
          )}
        </h2>

        {unassignedLoading ? (
          <div className="text-gray-500">Loading...</div>
        ) : unassignedStories.length === 0 ? (
          <div className="bg-gray-50 rounded-lg p-6 text-center text-gray-500">
            All stories have been assigned to streams.
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow divide-y">
            {unassignedStories.map((story: { id: string; title: string; claimCount?: number; materialCount?: number; modifiedAt?: string }) => {
              const suggestion = suggestionMap.get(story.id);
              return (
                <div key={story.id} className="p-3 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <Link to={`${orgPrefix}/story/${story.id}`} className="min-w-0 flex-1">
                      <h4 className="font-medium text-gray-900 truncate">{story.title}</h4>
                      <div className="text-sm text-gray-500 mt-0.5">
                        {story.claimCount || 0} claims · {story.materialCount || 0} materials
                      </div>
                    </Link>
                    <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" />
                  </div>
                  {suggestion?.suggestedStream && (
                    <div className="mt-2 flex items-center gap-2 text-sm flex-wrap">
                      <span className="text-gray-500">Suggested:</span>
                      <Link
                        to={`${orgPrefix}/stream/${suggestion.suggestedStream.id}`}
                        className="text-purple-600 hover:underline"
                      >
                        {suggestion.suggestedStream.title}
                      </Link>
                      {suggestion.sharedEntities.length > 0 && (
                        <span className="text-xs text-gray-400">
                          (via {suggestion.sharedEntities.slice(0, 2).map(e => e.name).join(', ')})
                        </span>
                      )}
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          handleAssignToStream(story.id, suggestion.suggestedStream!.id);
                        }}
                        disabled={assigningStoryId === story.id}
                        className="ml-auto px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200 disabled:opacity-50"
                      >
                        {assigningStoryId === story.id ? 'Adding...' : 'Add'}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
